import { useEffect, useState } from "react";
import { featureEnabled } from "@/lib/features";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, LogIn, LogOut, Loader2 } from "lucide-react";

function pad2(n) { return String(n).padStart(2, "0"); }

function fmtTime(iso) {
  const d = new Date(iso);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function sinceStr(iso) {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins}min`;
  return `${Math.floor(mins / 60)}h${pad2(mins % 60)}`;
}

export default function TimeClockWidget() {
  const [todayRecs, setTodayRecs] = useState(null); // null = loading
  const [loading, setLoading] = useState(false);
  const [, setTick] = useState(0);

  const load = async () => {
    // Ponto desligado: o widget some e não consulta (a consulta a cada 30 s
    // abria o popup "Recurso bloqueado" de novo a cada vez).
    if (!(await featureEnabled("attendance"))) { setTodayRecs(null); return; }
    try {
      const { data } = await api.get("/attendance/my-records");
      const today = new Date().toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" });
      const recs = (data || []).filter(r => {
        const d = new Date(r.timestamp);
        return d.toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" }) === today
            || (r.timestamp_local || "").slice(0, 10) === today;
      }).sort((a, b) => a.timestamp.localeCompare(b.timestamp));
      setTodayRecs(recs);
    } catch (e) {
      if ([402, 403].includes(e?.response?.status)) {
        setTodayRecs(null);
      } else {
        setTodayRecs([]);
      }
    }
  };

  useEffect(() => {
    load();
    const poll = setInterval(load, 30000);
    const tick = setInterval(() => setTick(t => t + 1), 1000);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, []);

  const doRecord = async () => {
    setLoading(true);
    try {
      const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
      const { data } = await api.post("/attendance/record", { device: isMobile ? "mobile" : "web" });
      toast.success(data.type === "entry" ? "Entrada registrada ✓" : "Saída registrada ✓");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao registrar ponto");
    } finally {
      setLoading(false);
    }
  };

  // Feature not available or still loading
  if (todayRecs === null) return null;

  const lastOfDay = todayRecs[todayRecs.length - 1];
  const isClockedIn = lastOfDay?.type === "entry";
  const nextType = isClockedIn ? "exit" : "entry";

  // Worked time today (entry/exit pairs)
  let workedMin = 0;
  for (let i = 0; i < todayRecs.length - 1; i++) {
    const a = todayRecs[i], b = todayRecs[i + 1];
    if (a.type === "entry" && b.type === "exit") {
      workedMin += Math.floor((new Date(b.timestamp) - new Date(a.timestamp)) / 60000);
    }
  }
  // If currently clocked in, add ongoing session
  if (isClockedIn && lastOfDay) {
    workedMin += Math.floor((Date.now() - new Date(lastOfDay.timestamp).getTime()) / 60000);
  }

  return (
    <Card className="border-neutral-200" data-testid="time-clock-widget">
      <CardContent className="p-4 md:p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-emerald-600" />
            <h3 className="font-semibold text-sm">Meu ponto</h3>
          </div>
          <div className="text-xs text-muted-foreground" data-testid="clock-status-label">
            {!isClockedIn && todayRecs.length === 0 && "Fora do expediente"}
            {!isClockedIn && todayRecs.length > 0 && (
              <span>
                Expediente encerrado · {Math.floor(workedMin / 60)}h{pad2(workedMin % 60)} hoje
              </span>
            )}
            {isClockedIn && (
              <span className="text-emerald-700 font-medium">
                Trabalhando há {sinceStr(lastOfDay.timestamp)}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            disabled={loading}
            className={`h-10 rounded-full flex-1 ${
              nextType === "entry"
                ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                : "border border-rose-300 text-rose-700 hover:bg-rose-50 bg-white"
            }`}
            variant={nextType === "entry" ? "default" : "outline"}
            onClick={doRecord}
            data-testid={nextType === "entry" ? "time-clock-in-btn" : "time-clock-out-btn"}
          >
            {loading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : nextType === "entry"
                ? <><LogIn className="w-4 h-4 mr-1.5" />Registrar entrada</>
                : <><LogOut className="w-4 h-4 mr-1.5" />Registrar saída</>
            }
          </Button>
        </div>

        {todayRecs.length > 0 && (
          <div className="mt-3 space-y-1">
            {todayRecs.map(r => (
              <div key={r.id} className="flex items-center justify-between text-xs px-2 py-1 rounded bg-neutral-50">
                <div className="flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${r.type === "entry" ? "bg-emerald-500" : "bg-rose-500"}`} />
                  <span className="text-neutral-600">{r.type === "entry" ? "Entrada" : "Saída"}</span>
                </div>
                <span className="tabular-nums text-neutral-700 font-medium">{fmtTime(r.timestamp)}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
